import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_CART_ID_KEY } from '../constants';
import { BasketItem, Options, Result } from '../types/basket';
import { fetchGuestCart, getFromStorage } from '../utils';
import { useEnvVariable } from '../contexts/EnvironmentContext';

function toErrorMessage(
  e: unknown,
  fallback = 'Failed to load basket',
): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return fallback;
  }
}

export function useGuestCart(options?: Options): Result {
  const {
    enabled = true,
    cartIdKey = DEFAULT_CART_ID_KEY,
    listenStorage = true,
  } = options ?? {};

  const { viteGuestCartEndpoint } = useEnvVariable();
  const [cartId, setCartId] = useState<string | null>(null);
  const [items, setItems] = useState<BasketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const value = getFromStorage(cartIdKey);
      setCartId(typeof value === 'string' ? value : null);
    } catch {
      setCartId(null);
    }
  }, [cartIdKey]);

  useEffect(() => {
    if (!listenStorage) return undefined;
    const onStorage = (e: StorageEvent) => {
      if (e.key === cartIdKey) setCartId(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [listenStorage, cartIdKey]);

  const hasCart = useMemo(() => Boolean(cartId), [cartId]);

  const refresh = useCallback(async () => {
    if (!enabled || !cartId) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { items } = await fetchGuestCart(cartId, viteGuestCartEndpoint);
      setItems(items);
    } catch (e: unknown) {
      setError(toErrorMessage(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, cartId, viteGuestCartEndpoint]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    cartId,
    items,
    count: items.length,
    hasCart,
    loading,
    error,
    refresh,
  };
}
